import { type FC, useState, useMemo } from 'react'
import { X, Download, Flag, Copy, CheckCircle2, Loader2 } from 'lucide-react'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Skeleton } from '@/components/ui/skeleton'
import type { User, UserTransaction } from '@/hooks/useUsers'
import {
    useUserPersonalDetail,
    useUserTransactionHistory,
} from '@/hooks/useUsers'
import { useDownloadUserTransactionHistory } from '@/hooks/useTransactions'
import { toast } from 'sonner'

interface AccountInformationModalProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    user: User | null
    onFlagAccount?: () => void
}

const CopyButton = ({ text }: { text: string }) => {
    const [copied, setCopied] = useState(false)

    const handleCopy = async (e: React.MouseEvent) => {
        e.stopPropagation()
        await navigator.clipboard.writeText(text)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }

    return (
        <button
            onClick={handleCopy}
            className="ml-1 inline-flex h-4 w-4 items-center justify-center text-muted-foreground hover:text-foreground"
            title="Copy"
        >
            {copied ? (
                <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
            ) : (
                <Copy className="h-3.5 w-3.5" />
            )}
        </button>
    )
}

const txnTypeColors = {
    Buy: 'bg-blue-100 text-blue-700 hover:bg-blue-100',
    Sell: 'bg-orange-100 text-orange-700 hover:bg-orange-100',
    Convert: 'bg-purple-100 text-purple-700 hover:bg-purple-100',
    Receive: 'bg-cyan-100 text-cyan-700 hover:bg-cyan-100',
    Send: 'bg-yellow-100 text-yellow-700 hover:bg-yellow-100',
}

const statusColors = {
    Completed: 'bg-green-100 text-green-700 hover:bg-green-100',
    Pending: 'bg-yellow-100 text-yellow-700 hover:bg-yellow-100',
    Failed: 'bg-red-100 text-red-700 hover:bg-red-100',
    Cancelled: 'bg-gray-100 text-gray-700 hover:bg-gray-100',
}

const tokenIcons: Record<string, string> = {
    BTC: '₿',
    USDT: '$',
    USDC: '$',
    ETH: 'Ξ',
    DOGE: 'Ð',
}

export const AccountInformationModal: FC<AccountInformationModalProps> = ({
    open,
    onOpenChange,
    user,
    onFlagAccount,
}) => {
    // Fetch user personal details when modal opens
    const { data: personalDetailData, isLoading: isLoadingDetails } =
        useUserPersonalDetail(user?.userId || '')

    // Fetch user transaction history
    const { data: transactionHistoryData, isLoading: isLoadingTransactions } =
        useUserTransactionHistory(user?.userId || '', { limit: 50 })

    const downloadHistoryMutation = useDownloadUserTransactionHistory()

    // Transform transaction history to match UserTransaction type
    const userTransactions = useMemo<UserTransaction[]>(() => {
        if (
            !transactionHistoryData?.data ||
            !Array.isArray(transactionHistoryData.data)
        )
            return []

        return transactionHistoryData.data.map(txn => ({
            id: txn.transactionId,
            date: new Date(txn.date).toLocaleDateString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric',
            }),
            txnType: (txn.transactionType === 'CREDIT'
                ? 'Receive'
                : txn.transactionType === 'DEBIT'
                  ? 'Send'
                  : 'Buy') as UserTransaction['txnType'],
            amount: txn.amountToken.split(' ')[0] || '0',
            token: txn.currency.toUpperCase() as UserTransaction['token'],
            status:
                txn.status === 'COMPLETED'
                    ? 'Completed'
                    : txn.status === 'PENDING'
                      ? 'Pending'
                      : txn.status === 'CANCELLED'
                        ? 'Cancelled'
                        : 'Failed',
        }))
    }, [transactionHistoryData])

    const handleDownloadHistory = () => {
        if (!user) return
        downloadHistoryMutation.mutate(
            { userId: user.userId },
            {
                onSuccess: () =>
                    toast.success(
                        'Transaction history downloaded successfully'
                    ),
                onError: () =>
                    toast.error('Failed to download transaction history'),
            }
        )
    }

    if (!user) return null

    const getInitials = (name: string) => {
        return name
            .split(' ')
            .map(n => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2)
    }

    const isFlagged = user.status === 'Flagged'
    const personalDetail = personalDetailData

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                showCloseButton={false}
                className="fixed left-1/2 top-1/2 z-50 h-auto max-h-[90vh] w-full max-w-[calc(100%-1rem)] -translate-x-1/2 -translate-y-1/2 overflow-hidden border-0 bg-transparent p-0 shadow-2xl data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right sm:top-6 sm:bottom-6 sm:left-auto sm:right-6 sm:translate-x-0 sm:translate-y-0 sm:max-h-[calc(100%-3rem)] sm:w-[980px] sm:max-w-[980px] sm:rounded-3xl"
            >
                <div className="flex h-full flex-col overflow-hidden bg-white sm:rounded-3xl">
                    {/* Header */}
                    <div className="flex items-center justify-between border-b bg-white px-6 py-4">
                        <div className="flex items-center gap-3">
                            <span className="rounded-full bg-[#E5F5F9] px-3 py-1 text-xs font-semibold text-[#0F172A]">
                                ACCOUNT INFORMATION
                            </span>
                        </div>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onOpenChange(false)}
                            className="h-8 w-8"
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </div>

                    {/* User Header Info */}
                    <div className="border-b bg-white px-6 py-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <Avatar className="h-10 w-10">
                                    <AvatarFallback className="bg-[#5FA8B4]/10 text-[#5FA8B4] font-semibold">
                                        {getInitials(user.accountName)}
                                    </AvatarFallback>
                                </Avatar>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <span className="font-semibold">
                                            {user.accountName}
                                        </span>
                                        <Badge
                                            variant="secondary"
                                            className={
                                                user.status === 'Active'
                                                    ? 'bg-green-100 text-green-700 hover:bg-green-100'
                                                    : 'bg-red-100 text-red-700 hover:bg-red-100'
                                            }
                                        >
                                            {user.status === 'Active' ? (
                                                <CheckCircle2 className="mr-1 h-3 w-3" />
                                            ) : (
                                                <Flag className="mr-1 h-3 w-3" />
                                            )}
                                            {user.status}
                                        </Badge>
                                    </div>
                                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                        <div className="flex items-center gap-1">
                                            <span>#{user.userId}</span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <span>{user.phoneNumber}</span>
                                            <CopyButton
                                                text={user.phoneNumber}
                                            />
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <span>{user.email}</span>
                                            <CopyButton text={user.email} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tabs */}
                    <Tabs defaultValue="account-info" className="flex-1">
                        <div className="border-b bg-white px-6">
                            <TabsList className="h-auto bg-transparent p-0">
                                <TabsTrigger
                                    value="account-info"
                                    className="rounded-none border-b-2 border-transparent px-4 pb-3 pt-4 data-[state=active]:border-[#5FA8B4] data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                                >
                                    Account Information
                                </TabsTrigger>
                                <TabsTrigger
                                    value="transactions"
                                    className="rounded-none border-b-2 border-transparent px-4 pb-3 pt-4 data-[state=active]:border-[#5FA8B4] data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                                >
                                    Transactions
                                </TabsTrigger>
                            </TabsList>
                        </div>

                        {/* Account Information Tab */}
                        <TabsContent
                            value="account-info"
                            className="mt-0 flex-1 overflow-y-auto bg-white p-6"
                        >
                            <div className="space-y-6">
                                {/* Personal Information */}
                                <div className="space-y-4 rounded-2xl bg-[#F7FAFC] p-5">
                                    <h3 className="text-sm font-semibold uppercase text-muted-foreground">
                                        Personal Information
                                    </h3>
                                    {isLoadingDetails ? (
                                        <div className="grid grid-cols-2 gap-x-8 gap-y-6 md:grid-cols-6">
                                            {Array.from({ length: 6 }).map(
                                                (_, i) => (
                                                    <div
                                                        key={i}
                                                        className="space-y-1"
                                                    >
                                                        <Skeleton className="h-4 w-20" />
                                                        <Skeleton className="h-5 w-full" />
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-2 gap-x-8 gap-y-6 md:grid-cols-6">
                                            <div className="space-y-1">
                                                <div className="text-xs text-muted-foreground">
                                                    Date Joined
                                                </div>
                                                <div className="text-sm font-semibold text-[#0F172A]">
                                                    {personalDetail?.joinedDate
                                                        ? new Date(
                                                              personalDetail.joinedDate
                                                          ).toLocaleDateString(
                                                              'en-US',
                                                              {
                                                                  month: '2-digit',
                                                                  day: '2-digit',
                                                                  year: 'numeric',
                                                              }
                                                          )
                                                        : user.dateJoined}
                                                </div>
                                            </div>
                                            <div className="space-y-1">
                                                <div className="text-xs text-muted-foreground">
                                                    Country of Residence
                                                </div>
                                                <div className="flex items-center gap-2 text-sm font-semibold text-[#0F172A]">
                                                    <span className="text-lg">
                                                        🌍
                                                    </span>
                                                    {personalDetail?.country
                                                        ? personalDetail.country
                                                        : 'N/A'}
                                                </div>
                                            </div>
                                            <div className="space-y-1 md:col-span-2">
                                                <div className="text-xs text-muted-foreground">
                                                    Residential Address
                                                </div>
                                                <div className="text-sm font-semibold text-[#0F172A]">
                                                    {personalDetail?.residentialAddress ||
                                                        'N/A'}
                                                </div>
                                            </div>
                                            <div className="space-y-1">
                                                <div className="text-xs text-muted-foreground">
                                                    Date of birth
                                                </div>
                                                <div className="text-sm font-semibold text-[#0F172A]">
                                                    {personalDetail?.dateOfBirth
                                                        ? new Date(
                                                              personalDetail.dateOfBirth
                                                          ).toLocaleDateString(
                                                              'en-US',
                                                              {
                                                                  month: '2-digit',
                                                                  day: '2-digit',
                                                                  year: 'numeric',
                                                              }
                                                          )
                                                        : 'N/A'}
                                                </div>
                                            </div>
                                            <div className="space-y-1">
                                                <div className="text-xs text-muted-foreground">
                                                    Gender
                                                </div>
                                                <div className="text-sm font-semibold text-[#0F172A]">
                                                    {personalDetail?.gender ===
                                                        'M' ||
                                                    personalDetail?.gender ===
                                                        'MALE'
                                                        ? 'Male'
                                                        : personalDetail?.gender ===
                                                                'F' ||
                                                            personalDetail?.gender ===
                                                                'FEMALE'
                                                          ? 'Female'
                                                          : 'N/A'}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Wallet Information */}
                                <div className="space-y-4 rounded-2xl bg-[#F7FAFC] p-5">
                                    <h3 className="text-sm font-semibold uppercase text-muted-foreground">
                                        Wallet Information
                                    </h3>
                                    {isLoadingDetails ? (
                                        <div className="grid grid-cols-2 gap-x-8 gap-y-6 md:grid-cols-6">
                                            {Array.from({ length: 6 }).map(
                                                (_, i) => (
                                                    <div
                                                        key={i}
                                                        className="space-y-1"
                                                    >
                                                        <Skeleton className="h-4 w-20" />
                                                        <Skeleton className="h-5 w-full" />
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-2 gap-x-8 gap-y-6 md:grid-cols-6">
                                            <div className="space-y-1">
                                                <div className="text-xs text-muted-foreground">
                                                    Wallet Address
                                                </div>
                                                <div className="flex items-center gap-1 text-sm font-semibold text-[#0F172A]">
                                                    <span className="font-mono">
                                                        {personalDetail?.walletAddress ||
                                                            user.walletAddress}
                                                    </span>
                                                    <CopyButton
                                                        text={
                                                            personalDetail?.walletAddress ||
                                                            user.walletAddress
                                                        }
                                                    />
                                                </div>
                                            </div>
                                            <div className="space-y-1">
                                                <div className="text-xs text-muted-foreground">
                                                    Total transactions
                                                </div>
                                                <div className="text-sm font-semibold text-[#0F172A]">
                                                    $
                                                    {personalDetail
                                                        ?.transactionSummary
                                                        ?.totalTransactions ||
                                                        '0'}
                                                </div>
                                            </div>
                                            <div className="space-y-1">
                                                <div className="text-xs text-muted-foreground">
                                                    Amount sent
                                                </div>
                                                <div className="text-sm font-semibold text-[#0F172A]">
                                                    $
                                                    {personalDetail
                                                        ?.transactionSummary
                                                        ?.amountSent || '0'}
                                                </div>
                                            </div>
                                            <div className="space-y-1">
                                                <div className="text-xs text-muted-foreground">
                                                    Amount received
                                                </div>
                                                <div className="text-sm font-semibold text-[#0F172A]">
                                                    $
                                                    {personalDetail
                                                        ?.transactionSummary
                                                        ?.amountReceived || '0'}
                                                </div>
                                            </div>
                                            <div className="space-y-1">
                                                <div className="text-xs text-muted-foreground">
                                                    Amount bought
                                                </div>
                                                <div className="text-sm font-semibold text-[#0F172A]">
                                                    $
                                                    {personalDetail
                                                        ?.transactionSummary
                                                        ?.amountBought || '0'}
                                                </div>
                                            </div>
                                            <div className="space-y-1">
                                                <div className="text-xs text-muted-foreground">
                                                    Amount Sold
                                                </div>
                                                <div className="text-sm font-semibold text-[#0F172A]">
                                                    $
                                                    {personalDetail
                                                        ?.transactionSummary
                                                        ?.amountSold || '0'}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Action Buttons */}
                                <div className="flex flex-wrap gap-3">
                                    <Button
                                        onClick={handleDownloadHistory}
                                        variant="outline"
                                        className="gap-2 rounded-full bg-white px-4 shadow-sm"
                                        disabled={
                                            downloadHistoryMutation.isPending
                                        }
                                    >
                                        {downloadHistoryMutation.isPending ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                            <Download className="h-4 w-4" />
                                        )}
                                        {downloadHistoryMutation.isPending
                                            ? 'Downloading...'
                                            : 'Download transaction history'}
                                    </Button>
                                    <Button
                                        onClick={onFlagAccount}
                                        variant="outline"
                                        className={
                                            isFlagged
                                                ? 'gap-2 rounded-full bg-[#EBFAEF] px-4 text-green-700 shadow-sm hover:bg-[#DCF4E4]'
                                                : 'gap-2 rounded-full bg-white px-4 text-red-600 shadow-sm hover:bg-red-50'
                                        }
                                    >
                                        <Flag className="h-4 w-4" />
                                        {isFlagged
                                            ? 'Un-flag account'
                                            : 'Flag account'}
                                    </Button>
                                </div>
                            </div>
                        </TabsContent>

                        {/* Transactions Tab */}
                        <TabsContent
                            value="transactions"
                            className="mt-0 flex-1 overflow-y-auto bg-white p-6"
                        >
                            <div className="space-y-4">
                                <h3 className="text-sm font-semibold uppercase text-muted-foreground">
                                    Transactions
                                </h3>

                                {/* Transactions Table */}
                                {isLoadingTransactions ? (
                                    <div className="space-y-3">
                                        <Skeleton className="h-12 w-full" />
                                        {Array.from({ length: 5 }).map(
                                            (_, i) => (
                                                <Skeleton
                                                    key={i}
                                                    className="h-16 w-full"
                                                />
                                            )
                                        )}
                                    </div>
                                ) : userTransactions.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-12 text-center">
                                        <p className="text-sm text-muted-foreground">
                                            No transactions found for this user
                                        </p>
                                    </div>
                                ) : (
                                    <div className="overflow-hidden rounded-lg border">
                                        <table className="w-full">
                                            <thead className="bg-muted/50">
                                                <tr>
                                                    <th className="px-4 py-3 text-left">
                                                        <input
                                                            type="checkbox"
                                                            className="rounded border-gray-300"
                                                        />
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-sm font-medium">
                                                        Txn ID
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-sm font-medium">
                                                        Date
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-sm font-medium">
                                                        Txn Type
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-sm font-medium">
                                                        Amount
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-sm font-medium">
                                                        Token
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-sm font-medium">
                                                        Status
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {userTransactions.map(txn => (
                                                    <tr
                                                        key={txn.id}
                                                        className="border-t hover:bg-muted/30"
                                                    >
                                                        <td className="px-4 py-3">
                                                            <input
                                                                type="checkbox"
                                                                className="rounded border-gray-300"
                                                            />
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <span className="font-mono text-xs">
                                                                #{txn.id}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-muted-foreground">
                                                            {txn.date}
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <Badge
                                                                variant="secondary"
                                                                className={
                                                                    txnTypeColors[
                                                                        txn
                                                                            .txnType
                                                                    ]
                                                                }
                                                            >
                                                                {txn.txnType}
                                                            </Badge>
                                                        </td>
                                                        <td className="px-4 py-3 text-sm font-medium">
                                                            {txn.amount}
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <div className="flex items-center gap-2">
                                                                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-orange-100 text-xs font-bold text-orange-600">
                                                                    {
                                                                        tokenIcons[
                                                                            txn
                                                                                .token
                                                                        ]
                                                                    }
                                                                </div>
                                                                <span className="text-sm font-medium">
                                                                    {txn.token}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <Badge
                                                                variant="secondary"
                                                                className={
                                                                    statusColors[
                                                                        txn
                                                                            .status
                                                                    ]
                                                                }
                                                            >
                                                                <CheckCircle2 className="mr-1 h-3 w-3" />
                                                                {txn.status}
                                                            </Badge>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {/* Action Buttons */}
                                <div className="flex flex-wrap gap-3">
                                    <Button
                                        onClick={handleDownloadHistory}
                                        variant="outline"
                                        className="gap-2 rounded-full bg-white px-4 shadow-sm"
                                        disabled={
                                            downloadHistoryMutation.isPending
                                        }
                                    >
                                        {downloadHistoryMutation.isPending ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                            <Download className="h-4 w-4" />
                                        )}
                                        {downloadHistoryMutation.isPending
                                            ? 'Downloading...'
                                            : 'Download transaction history'}
                                    </Button>
                                    <Button
                                        onClick={onFlagAccount}
                                        variant="outline"
                                        className={
                                            isFlagged
                                                ? 'gap-2 rounded-full bg-[#EBFAEF] px-4 text-green-700 shadow-sm hover:bg-[#DCF4E4]'
                                                : 'gap-2 rounded-full bg-white px-4 text-red-600 shadow-sm hover:bg-red-50'
                                        }
                                    >
                                        <Flag className="h-4 w-4" />
                                        {isFlagged
                                            ? 'Un-flag account'
                                            : 'Flag account'}
                                    </Button>
                                </div>
                            </div>
                        </TabsContent>
                    </Tabs>
                </div>
            </DialogContent>
        </Dialog>
    )
}
